import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';

import { ClubState, ClubMessage, ClubComponent, ClubComponentMetadata, ClubComponentId } from './club.state';
import * as ClubActions from './club.actions';
import { Observable, fromEvent, merge, Observer, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck } from 'rxjs/operators';
import {
    IClubState,
    IClubMember,
} from '@soposopo/sopo-models';

@Injectable()
export class ClubStateDispatcher {

    constructor(private store: Store<AppState>) {}

    /** Returns an Observable of non-null ClubState */
    public getClubState(): Observable<ClubState> {
        return this.store.select(state=> state.club).pipe(filter(clubState => clubState != null));
    }

    /** Returns an Observable of non-null, non-empty ClubData object */
    public getClubData(): Observable<IClubState> {
        return this.store
        .select(state => state.club)
        .pipe(
            filter(clubState => clubState != null),
            pluck(`clubData`),
            filter(clubData => {
                return clubData != null && clubData.id !== ''
            }),
        );
    }

    /**  Returns the current club ID */
    public getClubID(): Promise<string> {
        return firstValueFrom(this.getClubState()
        .pipe(
            map(clubState => clubState ? clubState.id ? clubState.id : '' : ''),
            filter((clubID) => clubID.length > 0)
        ));
    }

    public getClubIdObs(): Observable<string> {
        return this.getClubState()
        .pipe(
            map(clubState => clubState ? clubState.id ? clubState.id : '' : ''),
            filter((clubID) => clubID.length > 0),
            distinctUntilChanged()
        );
    }

    public getLastMessageId(): Observable<string> {
        return this.getClubState()
        .pipe(
            map(clubState => clubState.lastReadMessageId)
        );
    }

    public setLastMessageId(messageId: string, clubId: string) {
        this.store.dispatch(ClubActions.setChatLastMessageAction({messageId, clubId}));
    }

    /**  Returns the current club ID */
    public getClubCode(): Promise<string> {
        return firstValueFrom(this.getClubData()
            .pipe(
                map(clubState => clubState ? clubState.code ? clubState.code : '' : ''),
                filter(clubCode => clubCode.length > 0)
            ));
    }

    /** Returns an observable of the club's active component string */
    public getActivatedComponent(): Observable<ClubComponent> {
        return this.getClubState().pipe(pluck('activeComponent'));
    }

    public loadData(clubData: IClubState) {
        this.store.dispatch(ClubActions.clubLoadedAction({clubData}));
    }

    /** Load club messages */
    public loadClubMessages(clubMessages: ClubMessage[]) {
        this.store.dispatch(ClubActions.clubLoadMessagesAction({messages: clubMessages}));
    }

    /** Get club messages */
    public getClubMessages() {
        return this.getClubState().pipe(map(clubState => clubState.messages), distinctUntilChanged());
    }

    public setClubListening(to: boolean) {
        this.store.dispatch(ClubActions.setClubListeningAction({to}));
    }

    public clearMembersState() {
        this.store.dispatch(ClubActions.clearMembersState());
    }

    public setMembersListening(to: boolean) {
        this.store.dispatch(ClubActions.setMembersListeningAction({to}));
    }

    public membersLoaded(members: IClubMember[], numberOfMembers: number) {
        this.store.dispatch(ClubActions.membersLoadedAction({members, numberOfMembers}));
    }

    public memberModified(modifiedMember: IClubMember) {
        this.store.dispatch(ClubActions.memberModifiedAction({modifiedMember}));
    }

    public memberRemoved(removedMember: IClubMember, numberOfMembers: number) {
        this.store.dispatch(ClubActions.memberRemovedAction({removedMember, numberOfMembers}));
    }

    public enterClub(clubID: string): void {
        this.store.dispatch(ClubActions.clubEnteredAction({clubId: clubID}));
    }

    public leaveClub(): void {
        this.store.dispatch(ClubActions.clubLeftAction());
    }

    public activateComponent(componentId: ClubComponentId, metadata?: ClubComponentMetadata) {
        this.store.dispatch(ClubActions.activateClubComponent({component: {componentId, metadata}}));
    }

    public clearMessages(): void {
        this.store.dispatch(ClubActions.clearMessages());
    }
}
